/*
 * Turning a User-Agent string into something a person recognises.
 *
 * WHY THE CLIENT DOES THIS. Contract §0: the server sends facts, the client
 * computes display. `user_sessions.user_agent` is the fact — the exact string the
 * browser sent — and "Chrome on macOS" is a rendering of it. Keeping the raw
 * string on the wire means a future native client can describe it in its own
 * vocabulary, and it means a UA we cannot parse is still recoverable rather than
 * having been thrown away server-side.
 *
 * WHY THIS IS DELIBERATELY CRUDE. UA parsing is a bottomless pit — every browser
 * lies about being every other browser, for reasons going back to Netscape — and
 * a full parser is a dependency with a data file that goes stale. The job here is
 * narrow: help someone recognise their OWN devices in a list of two or three, so
 * they can tell which one to revoke. It does not need to be exhaustive; it needs
 * to never be CONFIDENTLY WRONG, which is why an unrecognised string falls back
 * to saying so rather than guessing.
 *
 * ORDER MATTERS IN BOTH LISTS BELOW, and that is the whole subtlety:
 *   - Edge's UA contains "Chrome" AND "Safari"; Chrome's contains "Safari".
 *     So the most specific claim is tested first and Safari is tested last.
 *   - iPadOS reports "Macintosh" in desktop mode, so iPad is tested before Mac.
 */

/** A browser name, most-specific first. The order is the algorithm. */
const BROWSERS: [needle: string, name: string][] = [
  ["Edg/", "Edge"],
  ["OPR/", "Opera"],
  ["Firefox/", "Firefox"],
  ["Chrome/", "Chrome"],
  ["Safari/", "Safari"],
];

/** An OS name, most-specific first, for the same reason. */
const PLATFORMS: [needle: string, name: string][] = [
  ["iPhone", "iPhone"],
  ["iPad", "iPad"],
  ["Android", "Android"],
  ["Mac OS X", "macOS"],
  ["Macintosh", "macOS"],
  ["Windows", "Windows"],
  ["CrOS", "ChromeOS"],
  ["Linux", "Linux"],
];

export interface DeviceDescription {
  /** "Chrome", "Safari" -- a product name, written as its maker writes it. */
  browser: string | null;
  /** "macOS", "iPhone" -- likewise. */
  platform: string | null;
  /** The raw string, always kept, so an unrecognised UA is still inspectable. */
  raw: string;
}

export function describeDevice(userAgent: string | null | undefined): DeviceDescription {
  const raw = userAgent ?? "";
  if (!raw) return { browser: null, platform: null, raw };
  return {
    browser: BROWSERS.find(([needle]) => raw.includes(needle))?.[1] ?? null,
    platform: PLATFORMS.find(([needle]) => raw.includes(needle))?.[1] ?? null,
    raw,
  };
}

/**
 * The sessions list's name for a device: "Chrome on macOS", "Safari on iPhone",
 * one half alone, or null when unrecognised.
 *
 * THE JOIN IS THE CATALOGUE'S (`settings.deviceOn`), not this file's. It was
 * written here in English, so every other language read "Chrome on macOS" in
 * the middle of its own sentences -- part of a screen translating and part not,
 * which is the defect the dates had (Kevin). The product names stay as their
 * makers write them, in every language.
 *
 * Returning null for neither rather than a half-guess is the point: the caller
 * then says "unrecognised device" honestly, and the user can still tell their
 * sessions apart by last-seen time.
 */
export function deviceLabel(
  d: DeviceDescription,
  t: (key: string, values: Record<string, string>) => string,
): string | null {
  if (d.browser && d.platform) return t("settings.deviceOn", { browser: d.browser, platform: d.platform });
  return d.platform ?? d.browser;
}

/**
 * How Settings names one session (stage 6 item 4): a native app's session by
 * the name its device gave when it signed in -- the name its owner chose --
 * and any other from its User-Agent. The iOS app labels them the same way,
 * through the core's `session_device`.
 */
export function sessionLabel(
  s: { device_name?: string; user_agent?: string },
  t: (key: string, values?: Record<string, string>) => string,
): string {
  const named = s.device_name?.trim();
  if (named) return named;
  return deviceLabel(describeDevice(s.user_agent), t) ?? t("settings.unknownDevice");
}
